import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from './OperatorAsyncLoadedSection.tsx';
import { Navigate } from 'react-router-dom';
import { DesktopLoginBackground } from '../../landing-page/LandingPage.tsx';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../../ui/modal/OperatorRoutedModal.tsx';
import { Box, Card, CardActionArea, Stack, Typography } from '@mui/material';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { css } from '@emotion/css';
import { useUpdate } from 'dg-web-shared/lib/Flux.tsx';
import * as AuthenticationActions from '../actions/AuthenticationActions.ts';
import type { JSX } from 'react';
import { ParkingportalLogo } from '../../mobile/HomePage.tsx';

interface OrganizationOperator {
    operatorName: string;
    operatorId: number;
    externalOperatorId: string;
}

function OperatorSelection({
    isMobile,
    renderModal,
}: {
    isMobile: boolean;
    renderModal: (data: {
        organizationOperators: OrganizationOperator[];
    }) => JSX.Element;
}) {
    const [organizationOperatorsState] = useServerFetch<
        OrganizationOperator[],
        { isMobile: boolean }
    >(
        () => ({
            url: `/ui-api/operator-account/organization-operator-logins?isMobile=${isMobile}`,
        }),
        { isMobile },
    );
    return (
        <OperatorAsyncLoadedSection
            requestState={organizationOperatorsState}
            render={organizationOperators => {
                if (organizationOperators.length === 1) {
                    return (
                        <Navigate
                            to={`/${organizationOperators[0].externalOperatorId}${isMobile ? '/mobile' : ''}`}
                        />
                    );
                }
                return renderModal({ organizationOperators });
            }}
        />
    );
}

export function OperatorSelectionDesktop() {
    const update = useUpdate();
    return (
        <OperatorSelection
            isMobile={false}
            renderModal={({ organizationOperators }) => (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        minWidth: '748px',
                    }}
                >
                    <DesktopLoginBackground>
                        <OperatorRoutedModal
                            variant={ModalVariant.SMALL}
                            render={() => (
                                <Box
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflowY: 'auto',
                                    }}
                                >
                                    <Box
                                        sx={theme => ({
                                            padding: theme.spacing(1.875, 3),
                                            color: theme.palette.common.white,
                                            backgroundColor:
                                                theme.palette.blue.main,
                                            fontSize: '26px',
                                            fontWeight: 500,
                                            textAlign: 'center',
                                        })}
                                    >
                                        <Localized
                                            de="Kontoauswahl"
                                            fr="Sélection du compte"
                                            it="Selezione conto"
                                            en="Account selection"
                                        />
                                    </Box>
                                    <Stack
                                        sx={{
                                            padding: '24px',
                                            flexGrow: 1,
                                        }}
                                        spacing={1}
                                    >
                                        {organizationOperators.map(operator => (
                                            <Stack
                                                key={
                                                    operator.externalOperatorId
                                                }
                                                direction="row"
                                                onClick={() => {
                                                    window.location.href = `/${operator.externalOperatorId}`;
                                                }}
                                                sx={theme => ({
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                    width: '100%',
                                                    padding: '8px',
                                                    fontSize: '18px',
                                                    cursor: 'pointer',
                                                    color: theme.palette.blue
                                                        .main,
                                                    '&:hover': {
                                                        background:
                                                            theme.palette.blue
                                                                .electric,
                                                    },
                                                })}
                                            >
                                                <Box>
                                                    {operator.operatorName}
                                                </Box>
                                                <Box>{operator.operatorId}</Box>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <Box
                                        onClick={() =>
                                            update(AuthenticationActions.logout)
                                        }
                                        sx={theme => ({
                                            display: 'flex',
                                            justifyContent: 'center',
                                            padding: '8px',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                            color: theme.palette.blue.main,
                                        })}
                                    >
                                        <Localized
                                            de="Ausloggen"
                                            fr="Se déconnecter"
                                            it="Disconnetti"
                                            en="Log out"
                                        />
                                    </Box>
                                    <Box
                                        sx={theme => ({
                                            padding: theme.spacing(1.875, 3),
                                            color: theme.palette.common.white,
                                            backgroundColor:
                                                theme.palette.blue.main,
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            textAlign: 'center',
                                        })}
                                    >
                                        <a
                                            className={css({ color: '#fff' })}
                                            href="mailto:info@parkingportal.ch"
                                        >
                                            info@parkingportal.ch
                                        </a>
                                    </Box>
                                </Box>
                            )}
                        />
                    </DesktopLoginBackground>
                </Box>
            )}
        />
    );
}

export function OperatorSelectionMobile() {
    const update = useUpdate();
    return (
        <OperatorSelection
            isMobile
            renderModal={({ organizationOperators }) => (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        overflowY: 'auto',
                    }}
                >
                    <Box
                        sx={theme => ({
                            display: 'flex',
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            padding: theme => theme.spacing(3),
                        })}
                    >
                        <Box
                            sx={{
                                width: 246,
                                height: 50,
                                paddingRight: theme => theme.spacing(2),
                            }}
                        >
                            <ParkingportalLogo />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            padding: theme => theme.spacing(4),
                            backgroundColor: theme => theme.palette.blue.light,
                            flexGrow: 1,
                        }}
                    >
                        <Typography
                            variant="h2"
                            sx={theme => ({
                                paddingTop: theme.spacing(1),
                                paddingBottom: theme.spacing(3),
                                textAlign: 'center',
                                marginBottom: theme.spacing(1),
                                color: theme => theme.palette.primary.main,
                                fontSize: '34px',
                                fontWeight: 700,
                            })}
                        >
                            <Localized
                                de="Kontoauswahl"
                                fr="Sélection du compte"
                                it="Selezione conto"
                                en="Account selection"
                            />
                        </Typography>
                        <Stack spacing={2}>
                            {organizationOperators.map(operator => (
                                <MenuItem
                                    key={operator.externalOperatorId}
                                    label={`${operator.operatorName} (${operator.operatorId})`}
                                    onClick={() => {
                                        window.location.href = `/${operator.externalOperatorId}/mobile`;
                                    }}
                                />
                            ))}
                        </Stack>
                        <Box
                            onClick={() => update(AuthenticationActions.logout)}
                            sx={theme => ({
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '8px',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                color: theme.palette.blue.main,
                            })}
                        >
                            <Localized
                                de="Abmelden"
                                fr="Déconnecter"
                                it="Esci"
                                en="Logout"
                            />
                        </Box>
                    </Box>
                </Box>
            )}
        />
    );
}

function MenuItem({ label, onClick }: { label: string; onClick: () => void }) {
    return (
        <Card elevation={0} sx={{ border: 0 }} onClick={onClick}>
            <CardActionArea
                className={'menuItemActionArea'}
                sx={theme => ({
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingX: theme.spacing(2),
                    paddingY: theme.spacing(3),
                    backgroundColor: theme =>
                        theme.palette.primary.contrastText,
                    ['&:hover']: {
                        backgroundColor: theme => theme.palette.primary.light,
                    },
                })}
            >
                <Typography
                    sx={{
                        marginLeft: '24px',
                        fontSize: '20px',
                        fontWeight: 700,
                        color: theme => theme.palette.primary.main,
                    }}
                >
                    {label}
                </Typography>
            </CardActionArea>
        </Card>
    );
}
